import request from '../request';
import reservations from '../../services/gma/reservations';
import {batchActions} from '../../utils/redux';
import {getAdditionalInfoSetOpts, getAdditionalInfoUnsetPath} from '../../utils/app/reservations';
import {types} from '../../utils/js/types';

import {GMI, STATE} from '../../constants';

const {COMMIT} = STATE.UI.RESERVATIONS;
const DRIVER_INFO = COMMIT.concat(['driver_info']);
const AIRLINE_INFO = COMMIT.concat(['airline_information']);

export const commitPaths = {
  auto_comms_consent_opt_in: COMMIT.concat(["auto_comms_consent_opt_in"]),
  driverInfo: {
    email_address: DRIVER_INFO.concat(['email_address']),
    first_name: DRIVER_INFO.concat(['first_name']),
    last_name: DRIVER_INFO.concat(['last_name']),
    phone: DRIVER_INFO.concat(['phone']),
    request_email_promotions: DRIVER_INFO.concat(['request_email_promotions'])
  },
  airlineInfo: {
    code: AIRLINE_INFO.concat(['code']),
    flight_number: AIRLINE_INFO.concat(['flight_number'])
  },
  billing_account: COMMIT.concat(['billing_account']),
  billing_account_type: COMMIT.concat(['billing_account_type']),
  payment_ids: COMMIT.concat(['payment_ids']),
  payment_id: COMMIT.concat(['payment_id']),
  payment_method: COMMIT.concat(['payment_method']),
  additionalInfo: COMMIT.concat(['additional_information']),
  partner_reward_program_code: COMMIT.concat(['partner_reward_program_code']),
  partner_reward_program_member_id: COMMIT.concat(['partner_reward_program_member_id'])
};

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResDriverInfoEmail() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.driverInfo.email_address};
}

/**
 * @private
 * @param {String} email_address
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResDriverInfoEmail(email_address) {
  return {
    type: GMI.SET_GMI_STATE,
    path: commitPaths.driverInfo.email_address,
    value: email_address,
  };
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResDriverInfoFirstName() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.driverInfo.first_name};
}

/**
 * @private
 * @param {String} first_name
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResDriverInfoFirstName(first_name) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.driverInfo.first_name, value: first_name};
}
/**
 * @private
 * @param {Boolean} authorized
 * @returns {Object} {type: string, path: [], value: boolean}
 */
function setResConsentOptIn(authorized) {
  return {
    type: GMI.SET_GMI_STATE,
    path: commitPaths.auto_comms_consent_opt_in,
    value: authorized,
  };
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResDriverInfoLastName() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.driverInfo.last_name};
}

/**
 * @private
 * @param {String} last_name
 * @returns {Object} {type: string, path: [], value: string}
 */
function setResDriverInfoLastName(last_name) {
  return {
    type: GMI.SET_GMI_STATE,
    path: commitPaths.driverInfo.last_name,
    value: last_name,
  };
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResDriverInfoPhone() {
  return { type: GMI.DELETE_GMI_STATE, path: commitPaths.driverInfo.phone };
}

/**
 * @private
 * @param {Object} phone {phone_number: string}
 * @returns {Object} {type: string, path: [], value: object}
 */
function setResDriverInfoPhone(phone) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.driverInfo.phone, value: phone};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResDriverInfoRequestEmailPromotions() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.driverInfo.request_email_promotions};
}

/**
 * @private
 * @param {Boolean} request_email_promotions
 * @returns {Object} {type: string, path: [], value: boolean}
 */
function setResDriverInfoRequestEmailPromotions(request_email_promotions) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.driverInfo.request_email_promotions, value: request_email_promotions};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResAirlineInfoCode() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.airlineInfo.code};
}

/**
 * @private
 * @param {String} code
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResAirlineInfoCode(code) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.airlineInfo.code, value: code};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResAirlineInfoFlightNumber() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.airlineInfo.flight_number};
}

/**
 * @private
 * @param {String} flight_number
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResAirlineInfoFlightNumber(flight_number) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.airlineInfo.flight_number, value: flight_number};
}

/**
 * @private
 * @desc Set additional info by array of objects
 * @param {Array} value
 * @returns {Object} {type: string, path: (Array), value: (Array)}
 */
function setResAdditionalInfoBatchAction(value) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.additionalInfo, value};
}

/**
 * @private
 * @desc Clear all additional info, or by id if present in state
 * @param {String} id
 * @returns {Function}
 */
function clearResAdditionalInfo(id) {
  return (dispatch, getState) => {
    const path = getAdditionalInfoUnsetPath({id, state: getState(), basePath: commitPaths.additionalInfo});
    if (path) {
      dispatch({type: GMI.DELETE_GMI_STATE, path});
    }
  };
}

/**
 * @private
 * @param {Object} info {id: string, value: string}
 * @returns {Function}
 */
function setResAdditionalInfo(info) {
  return (dispatch, getState) => dispatch(Object.assign(
    {type: GMI.SET_GMI_STATE},
    getAdditionalInfoSetOpts({info, state: getState(), basePath: commitPaths.additionalInfo})
  ));
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResBillingAccount() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.billing_account};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResBillingAccountType() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.billing_account_type};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResPaymentIds() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.payment_ids};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResPaymentId() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.payment_id};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResPaymentMethod() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.payment_method};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResPartnerRewardProgramCode() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.partner_reward_program_code};
}

/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResPartnerRewardProgramMemberId() {
  return {type: GMI.DELETE_GMI_STATE, path: commitPaths.partner_reward_program_member_id};
}
/**
 * @private
 * @returns {Object} {type: string, path: []}
 */
function clearResConsentOptIn() {
  return {
    type: GMI.DELETE_GMI_STATE,
    path: commitPaths.auto_comms_consent_opt_in,
  };
}

/**
 * @private
 * @param {String} billing_account
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResBillingAccount(billing_account) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.billing_account, value: billing_account};
}

/**
 * @private
 * @param {String} billing_account_type
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResBillingAccountType(billing_account_type) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.billing_account_type, value: billing_account_type};
}

/**
 * @private
 * @param {String} partner_reward_program_code
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResPartnerRewardProgramCode(partner_reward_program_code) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.partner_reward_program_code, value: partner_reward_program_code};
}

/**
 * @private
 * @param {String} partner_reward_program_member_id
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResPartnerRewardProgramMemberId(partner_reward_program_member_id) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.partner_reward_program_member_id, value: partner_reward_program_member_id};
}

/**
 * @private
 * @param {String|Array} payment_ids
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResPaymentIds(payment_ids) {
  const value = types(payment_ids).isArray ? payment_ids : [payment_ids];
  return {type: GMI.SET_GMI_STATE, path: commitPaths.payment_ids, value};
}

/**
 * @private
 * @param {String} payment_id
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResPaymentId(payment_id) {
  return {type: GMI.SET_GMI_STATE, path: commitPaths.payment_id, value: payment_id};
}

/**
 * @private
 * @param {String|Array} payment_id
 * @returns {Object} {type: string, path: [], value: ''}
 */
function setResPaymentMethod(payment_id) {
  const value = !!payment_id && {payment_session_id: types(payment_id).isArray ? payment_id[0] : payment_id} || payment_id;
  return {type: GMI.SET_GMI_STATE, path: commitPaths.payment_method, value};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearResCommitState() {
  return {type: GMI.SET_GMI_STATE, path: COMMIT, value: {}};
}

/**
 * @private
 * @desc destructure args based on format of gmi.ui.reservations.commit
 * @param {Object} args
 * @returns {Object} {type: string, path: [], value: object}
 */
function destructureCommitParamsFromArgs(args) {
  const {
    additional_information,
    airline_information = {},
    billing_account,
    billing_account_type,
    auto_comms_consent_opt_in,
    driver_info = {},
    payment_ids
  } = args;

  const {
    email_address,
    first_name,
    last_name,
    phone,
    request_email_promotions
  } = driver_info;

  const {
    code,
    flight_number
  } =  airline_information;

  return {
    additional_information,
    billing_account,
    billing_account_type,
    code,
    email_address,
    first_name,
    flight_number,
    last_name,
    payment_ids,
    phone,
    request_email_promotions,
    auto_comms_consent_opt_in,
  };
}


/**
 * @private
 * @param {Object} [args]
 * @param {Boolean} [format]
 * @returns {Function}
 */
function setResCommitState(args = {}, format = true) {
  return dispatch => {
    const batchedActions = [];
    const {
      additional_information,
      billing_account,
      billing_account_type,
      code,
      email_address,
      first_name,
      flight_number,
      last_name,
      payment_ids,
      payment_id,
      phone,
      request_email_promotions,
      partner_reward_program_code,
      partner_reward_program_member_id,
      auto_comms_consent_opt_in,
    } = format ? destructureCommitParamsFromArgs(args) : args;

    // Additional Info
    if (types(additional_information).isArray) batchedActions.push(setResAdditionalInfoBatchAction(additional_information));

    // Airline Info
    if (code) batchedActions.push(setResAirlineInfoCode(code));
    if (flight_number) batchedActions.push(setResAirlineInfoFlightNumber(flight_number));

    if (auto_comms_consent_opt_in) {
      batchedActions.push(setResConsentOptIn(auto_comms_consent_opt_in));
    }

    // Driver Info
    if (first_name) batchedActions.push(setResDriverInfoFirstName(first_name));
    if (last_name) batchedActions.push(setResDriverInfoLastName(last_name));
    if (email_address) batchedActions.push(setResDriverInfoEmail(email_address));
    if (phone) batchedActions.push(setResDriverInfoPhone(phone));
    if (types(request_email_promotions).isDefined) batchedActions.push(setResDriverInfoRequestEmailPromotions(request_email_promotions));

    // Method Of Payment
    if (billing_account) batchedActions.push(setResBillingAccount(billing_account));
    if (billing_account_type) batchedActions.push(setResBillingAccountType(billing_account_type));
    if (payment_ids){
      batchedActions.push(setResPaymentIds(payment_ids));
      batchedActions.push(setResPaymentMethod(payment_ids));
    }
    if (payment_id){
      batchedActions.push(setResPaymentId(payment_id));
      batchedActions.push(setResPaymentMethod(payment_id));
    }

    // Partner Rewards Program
    if (partner_reward_program_code) batchActions.push(setResPartnerRewardProgramCode(partner_reward_program_code));
    if (partner_reward_program_member_id) batchActions.push(setResPartnerRewardProgramMemberId(partner_reward_program_member_id));

    if (batchedActions.length) return dispatch(batchActions(...batchedActions));

    return null;
  };
}

export const commitState = {
  /**
   * @name set
   * @desc A global setter for all reservation.commit payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @param {Object} [opts]
   * @returns {Function}
   * @memberof actions.reservations.commitState
   * @example
   *
   * // set first name
   * store.dispatch(actions.reservations.commitState.set('first_name', {first_name: 'first'}));
   *
   * // set last name
   * store.dispatch(actions.reservations.commitState.set('last_name', {last_name: 'last'}));
   *
   * // set email address
   * store.dispatch(actions.reservations.commitState.set('email_address', {email_address: 'test@test.com'}));
   *
   * // set phone
   * store.dispatch(actions.reservations.commitState.set('phone', {phone: {phone_number: 123456789}}));
   *
   * // set request email promotions
   * store.dispatch(actions.reservations.commitState.set('request_email_promotions', {request_email_promotions: false}));
   *
   * // set code
   * store.dispatch(actions.reservations.commitState.set('code', {code: '1234'}));
   *
   * // set flight number
   * store.dispatch(actions.reservations.commitState.set('flight_number', {flight_number: '12345678'}));
   *
   * // set additional information
   * store.dispatch(actions.reservations.commitState.set('additional_information', {id: '1234', value: 'Info'}));
   *
   * // set billing account
   * store.dispatch(actions.reservations.commitState.set('billing_account', {billing_account: 'account'}));
   *
   * // set billing account type
   * store.dispatch(actions.reservations.commitState.set('billing_account_type', {billing_account_type: 'type'}));
   *
   * // set partner rewards code
   * store.dispatch(actions.reservations.commitState.set('partner_reward_program_code', {partner_reward_program_code: 'code'}));
   *
   * // set partner rewards member id
   * store.dispatch(actions.reservations.commitState.set('partner_reward_program_member_id', {partner_reward_program_member_id: 'member id'}));
   *
   * // set payment_ids
   * store.dispatch(actions.reservations.commitState.set('payment_ids', {payment_ids: 'id'}));
   *
   * // set payment_id
   * store.dispatch(actions.reservations.commitState.set('payment_id', {payment_id: 'id'}));
   *
   * // set driver_info
   * store.dispatch(actions.reservations.commitState.set(null, {driver_info}));
   *
   * // set billing info
   * store.dispatch(actions.reservations.commitState.set(null, {billing_account, billing_account_type}));
   *
   * // set partner rewards
   * store.dispatch(actions.reservations.commitState.set(null, {partner_reward_program_code, partner_reward_program_member_id}));
   *
   * // set airline_information
   * store.dispatch(actions.reservations.commitState.set(null, {airline_information}));
   *
   * // set args in state format
   * store.dispatch(actions.reservations.commitState.set(null, commit));
   *
   * // set args in passed format
   * store.dispatch(actions.reservations.commitState.set(null, commit, {format: false}));
   */
  set(type = null, args, opts = {}) {
    return (dispatch) => {
      if (types(type).isString) {

        // Additional Info
        if (type === 'additional_information') {
          dispatch(setResAdditionalInfo(args.additional_information));

          // Airline Info
        } else if (type === 'code') {
          dispatch(setResAirlineInfoCode(args.code));
        } else if (type === 'flight_number') {
          dispatch(setResAirlineInfoFlightNumber(args.flight_number));

          // Driver Info
        } else if (type === 'first_name') {
          dispatch(setResDriverInfoFirstName(args.first_name));
        } else if (type === 'last_name') {
          dispatch(setResDriverInfoLastName(args.last_name));
        } else if (type === 'email_address') {
          dispatch(setResDriverInfoEmail(args.email_address));
        }  else if (type === 'phone') {
          dispatch(setResDriverInfoPhone(args.phone));
        } else if (type === 'request_email_promotions') {
          dispatch(setResDriverInfoRequestEmailPromotions(args.request_email_promotions));

          // Method Of Payment
        } else if (type === 'billing_account') {
          dispatch(setResBillingAccount(args.billing_account));
        } else if (type === 'billing_account_type') {
          dispatch(setResBillingAccountType(args.billing_account_type));
        } else if (type === 'payment_ids') {
          dispatch(setResPaymentIds(args.payment_ids));
          dispatch(setResPaymentMethod(args.payment_ids));
        } else if (type === 'payment_id') {
          dispatch(setResPaymentId(args.payment_id));
          dispatch(setResPaymentMethod(args.payment_id));
          // Partner Rewards
        } else if (type === 'partner_reward_program_code') {
          dispatch(setResPartnerRewardProgramCode(args.partner_reward_program_code));
        } else if (type === 'partner_reward_program_member_id') {
          dispatch(setResPartnerRewardProgramMemberId(args.partner_reward_program_member_id));

        } else if (type === "auto_comms_consent_opt_in") {
          dispatch(setResConsentOptIn(args.auto_comms_consent_opt_in));
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        // return a promise
        return dispatch(setResCommitState(args, opts.format));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for all reservation.commit payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.reservations.commitState
   * @example
   *
   * // clear email address
   * store.dispatch(actions.reservations.commitState.unset('email_address'));
   *
   * // clear first name
   * store.dispatch(actions.reservations.commitState.unset('first_name'));
   *
   * // clear last name
   * store.dispatch(actions.reservations.commitState.unset('last_name'));
   *
   * // clear phone
   * store.dispatch(actions.reservations.commitState.unset('phone'));
   *
   * // clear request email promotions
   * store.dispatch(actions.reservations.commitState.unset('request_email_promotions'));
   *
   * // clear code
   * store.dispatch(actions.reservations.commitState.unset('code'));
   *
   * // clear flight number
   * store.dispatch(actions.reservations.commitState.unset('flight_number'));
   *
   * // clear additional information
   * store.dispatch(actions.reservations.commitState.unset('additional_information', {id: '1234'}));
   *
   * // clear billing account
   * store.dispatch(actions.reservations.commitState.unset('billing_account'));
   *
   * // clear billing account type
   * store.dispatch(actions.reservations.commitState.unset('billing_account_type'));
   *
   * // clear payment_ids
   * store.dispatch(actions.reservations.commitState.unset('payment_ids'));
   *
   * // clear payment_id
   * store.dispatch(actions.reservations.commitState.unset('payment_id'));
   *
   * // clear partner rewards code
   * store.dispatch(actions.reservations.commitState.unset('partner_reward_program_code'));
   *
   * // clear partner rewards member id
   * store.dispatch(actions.reservations.commitState.unset('partner_reward_program_member_id'));
   *
   * // clear consent opt int
   * store.dispatch(actions.reservations.commitState.unset('auto_comms_consent_opt_in'));
   */
  unset(type = null, args = {}) {
    return (dispatch) => {
      if (types(type).isString) {

        // Additional Info
        if (type === 'additional_information') {
          dispatch(clearResAdditionalInfo(args.id));

          // Airline Info
        } else if (type === 'code') {
          dispatch(clearResAirlineInfoCode());
        } else if (type === 'flight_number') {
          dispatch(clearResAirlineInfoFlightNumber());

          // Driver Info
        } else if (type === 'first_name') {
          dispatch(clearResDriverInfoFirstName());
        } else if (type === 'last_name') {
          dispatch(clearResDriverInfoLastName());
        } else if (type === 'email_address') {
          dispatch(clearResDriverInfoEmail());
        } else if (type === 'phone') {
          dispatch(clearResDriverInfoPhone());
        } else if (type === 'request_email_promotions') {
          dispatch(clearResDriverInfoRequestEmailPromotions());

          // Method Of Payment
        } else if (type === 'billing_account') {
          dispatch(clearResBillingAccount());
        } else if (type === 'billing_account_type') {
          dispatch(clearResBillingAccountType());
        } else if (type === 'payment_ids') {
          dispatch(clearResPaymentIds());
          dispatch(clearResPaymentMethod());
        } else if (type === 'payment_id') {
          dispatch(clearResPaymentId());
          dispatch(clearResPaymentMethod());

          // Partner Rewards
        } else if (type === 'partner_reward_program_code') {
          dispatch(clearResPartnerRewardProgramCode());
        } else if (type === 'partner_reward_program_member_id') {
          dispatch(clearResPartnerRewardProgramMemberId());
        } else if (type === "auto_comms_consent_opt_in") {
          dispatch(clearResConsentOptIn());
        } else {
          console.warn(`${type} is not a supported state type`);
        }
      } else {
        dispatch(clearResCommitState());
      }
    };
  }
};

/**
 * @name commit
 * @desc Make the GMA reservations/commit call with state data
 * @param {Object} opts
 * @param {Object} [data] `{params}` Will get merged with `getState().getIn(COMMIT).toJS()` object
 * @param {Object} [requestOpts]
 * @param {function} [mergeFunc] Custom merge function that will be passed `(gmiPayload, data)` and must return {@link actions.CommitPostData}
 * @returns {Function}
 * @see `mergeFunc` must return data in the format used by the services - {@link actions.CommitPostData}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.commit());
 * store.dispatch(actions.reservations.commit({source_code: 'AMOBILEAPP'}));
 * store.dispatch(actions.reservations.commit({online_check_in: true}));
 * store.dispatch(actions.reservations.commit({auto_check_in: true}));
 * store.dispatch(actions.reservations.commit({accept_branch_local_addenda: true}));
 * @example <caption>Using or not using the merge function</caption>
 *  // (1) Note internally **by default** we use:
 *  Object.assign(data, {payload})
 *
 *  // (2) instead, define your own merge function
 *  function myDataIsBetter(payload, data){
 *   return Object.assign({params: {}}, {payload}, {payload: data.payload}); // overide GMI payload
 *  }
 *
 *  // (3) call commit
 *  dispatch(commit({}, {payload:{one:true}}, requestOpts, myDataIsBetter));
 *
 *  // (4) then, internally, this will:
 *  const payload = getState().getIn(COMMIT).toJS();
 *  // assume payload = {one:false}
 *  const newData = myDataIsBetter(payload, myData);
 *  // newData = {params: {}, payload: {one:true}}
 *  return dispatch(
 *    request({
 *      newData,
 *      service,
 *      opts
 *    })
 *  )
 */
export function commit(opts = {}, data, requestOpts = {}, mergeFunc) {
  const { source_code, online_check_in = false, auto_check_in = false, accept_branch_local_addenda = false } = opts;
  return (dispatch, getState) => {
    const payload = getState().getIn(COMMIT).toJS();
    if (!types(data).isObject) data = {};
    if (payload.driver_info && source_code)
      Object.assign(payload.driver_info, { source_code });
    if (online_check_in) Object.assign(payload, { online_check_in });
    if (auto_check_in) Object.assign(payload, { auto_check_in });
    if (accept_branch_local_addenda)
      Object.assign(payload, { accept_branch_local_addenda });
    /**
     * @type {CommitPostData}
     */
    const postData = !!mergeFunc
      ? mergeFunc(payload, data)
      : Object.assign(data, { payload });

    if (!postData.params && !postData.payload) {
      return Promise.reject(new Error('actions.reservations.commit invalid data format.'));
    }
    return dispatch(
      request({
        data: postData,
        service: reservations.commit,
        opts: requestOpts,
      })
    );
  };
}
